import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Alert,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  ViewDay as DayIcon,
  ViewWeek as WeekIcon,
  ViewModule as MonthIcon,
  ViewList as ListIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { Schedule } from '../types';

// Helper function to get the start of the week (Monday)
const getWeekStartDate = (date: Date): string => {
  const d = new Date(date);
  const day = d.getDay();
  // Monday is day 1, Sunday is day 0
  // If it's Sunday (0), we want to go back 6 days to get to Monday
  // If it's Monday (1), we want to go back 0 days
  // If it's Tuesday (2), we want to go back 1 day
  // If it's Wednesday (3), we want to go back 2 days, etc.
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  
  // Use local date formatting to avoid timezone issues
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
};

// Helper function to get dates in a week
const getWeekDates = (weekStart: string): string[] => {
  const dates = [];
  // Parse the date string to avoid timezone issues
  const [year, month, day] = weekStart.split('-').map(Number);
  const selectedDate = new Date(year, month - 1, day);
  
  // Find the Monday of the week containing the selected date
  const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to get to Monday
  
  const mondayDate = new Date(selectedDate);
  mondayDate.setDate(selectedDate.getDate() + daysToMonday);
  
  // Generate 7 days starting from Monday
  for (let i = 0; i < 7; i++) {
    const date = new Date(mondayDate);
    date.setDate(mondayDate.getDate() + i);
    
    // Use local date formatting to avoid timezone issues
    const dateYear = date.getFullYear();
    const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
    const dateDay = String(date.getDate()).padStart(2, '0');
    dates.push(`${dateYear}-${dateMonth}-${dateDay}`);
  }
  return dates;
};

// Helper function to get dates in a month (including padding days for calendar grid)
const getMonthDates = (month: string): string[] => {
  const dates = [];
  const year = parseInt(month.split('-')[0]);
  const monthNum = parseInt(month.split('-')[1]) - 1;
  
  // Get first day of the month
  const firstDay = new Date(year, monthNum, 1);
  
  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  // Adjust for Monday start: if Sunday (0), we want 6 days before; if Monday (1), we want 0 days before
  const firstDayOfWeek = firstDay.getDay();
  const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  
  // Start from the Monday of the week containing the first day of the month
  const startDate = new Date(firstDay);
  startDate.setDate(firstDay.getDate() - daysToSubtract);
  
  // Generate 42 days (6 weeks) to ensure we cover the entire month
  for (let i = 0; i < 42; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    
    // Use local date formatting to avoid timezone issues
    const dateYear = date.getFullYear();
    const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
    const dateDay = String(date.getDate()).padStart(2, '0');
    dates.push(`${dateYear}-${dateMonth}-${dateDay}`);
  }
  
  return dates;
};

const MySchedulePage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [selectedWeek, setSelectedWeek] = useState<string>(() => {
    const today = new Date();
    // Ensure we get the correct local date without timezone issues
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    const localToday = new Date(year, month, day);
    const weekStart = getWeekStartDate(localToday);
    console.log('🔍 MySchedulePage - selectedWeek initialization:', {
      today: today.toISOString(),
      localToday: localToday.toISOString(),
      weekStart: weekStart
    });
    return weekStart;
  });
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [dailySchedules, setDailySchedules] = useState<Schedule[]>([]);
  const [weeklySchedules, setWeeklySchedules] = useState<Schedule[]>([]);
  const [monthlySchedules, setMonthlySchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  const loadDailySchedule = useCallback(async () => {
    if (!selectedDate) return;

    try {
      setLoading(true);
      const response = await apiClient.schedules.getMyScheduleForDate(selectedDate);
      setDailySchedules(response.data.data);
    } catch (error) {
      console.error('Error loading daily schedule:', error);
      setError('Failed to load daily schedule');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const loadWeeklySchedule = useCallback(async () => {
    if (!selectedWeek) return;

    try {
      setLoading(true);
      const weekDates = getWeekDates(selectedWeek);
      const startDate = weekDates[0];
      const endDate = weekDates[6];
      
      const response = await apiClient.schedules.getMyScheduleRange(startDate, endDate);
      setWeeklySchedules(response.data.data);
    } catch (error) {
      console.error('Error loading weekly schedule:', error);
      setError('Failed to load weekly schedule');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek]);

  const loadMonthlySchedule = useCallback(async () => {
    if (!selectedMonth) return;

    try {
      setLoading(true);
      const monthDates = getMonthDates(selectedMonth);
      const startDate = monthDates[0];
      const endDate = monthDates[monthDates.length - 1];
      
      const response = await apiClient.schedules.getMyScheduleRange(startDate, endDate);
      setMonthlySchedules(response.data.data);
    } catch (error) {
      console.error('Error loading monthly schedule:', error);
      setError('Failed to load monthly schedule');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  // Ensure selectedMonth is properly initialized with current month
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
    const currentMonthString = `${currentYear}-${currentMonth}`;
    
    // Only update if selectedMonth is not set or is different from current month
    if (!selectedMonth || selectedMonth !== currentMonthString) {
      setSelectedMonth(currentMonthString);
    }
  }, [selectedMonth]);

  // Sync selectedWeek with selectedDate when date changes
  useEffect(() => {
    if (selectedDate) {
      // Parse the date string to avoid timezone issues
      const [year, month, day] = selectedDate.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      const weekStart = getWeekStartDate(localDate);
      console.log('🔍 MySchedulePage - selectedWeek sync from selectedDate:', {
        selectedDate,
        localDate: localDate.toISOString(),
        weekStart
      });
      setSelectedWeek(weekStart);
    }
  }, [selectedDate]);

  // Load data based on active tab and view mode
  useEffect(() => {
    if (viewMode === 'list') {
      if (activeTab === 0 && selectedDate) {
        loadDailySchedule();
      } else if (activeTab === 1 && selectedWeek) {
        loadWeeklySchedule();
      } else if (activeTab === 2 && selectedMonth) {
        loadMonthlySchedule();
      }
    } else if (viewMode === 'calendar') {
      // Load data based on active tab for calendar view
      if (activeTab === 1 && selectedWeek) {
        // Load weekly data for weekly calendar view
        loadWeeklySchedule();
      } else if (selectedMonth) {
        // Load monthly data for monthly calendar view
        loadMonthlySchedule();
      }
    }
  }, [activeTab, viewMode, selectedDate, selectedWeek, selectedMonth, loadDailySchedule, loadWeeklySchedule, loadMonthlySchedule]);

  const formatDate = (dateString: string) => {
    // Parse the date string to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatMonthDate = (dateString: string) => {
    // Parse the date string to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return {
      day: date.getDate(),
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' })
    };
  };

  const getSchedulesForDate = (date: string, schedules: Schedule[]) => {
    return schedules.filter(schedule => schedule.date === date);
  };


  if (loading && dailySchedules.length === 0 && weeklySchedules.length === 0 && monthlySchedules.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        My Schedule
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        View your personal schedule for different time periods
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for different views */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab 
              icon={<DayIcon />} 
              label="Daily View" 
              iconPosition="start"
            />
            <Tab 
              icon={<WeekIcon />} 
              label="Weekly View" 
              iconPosition="start"
            />
            <Tab 
              icon={<MonthIcon />} 
              label="Monthly View" 
              iconPosition="start"
            />
          </Tabs>
        </Box>
        <CardContent>
          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant={viewMode === 'list' ? 'contained' : 'outlined'}
              startIcon={<ListIcon />}
              onClick={() => setViewMode('list')}
              sx={{ mr: 1 }}
            >
              List View
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
              startIcon={<CalendarIcon />}
              onClick={() => setViewMode('calendar')}
            >
              Calendar View
            </Button>
          </Box>
          
          {/* Daily View Controls */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Date:
              </Typography>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </Box>
          )}

          {/* Weekly View Controls */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Week Start Date:
              </Typography>
              <input
                type="date"
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </Box>
          )}

          {/* Monthly View Controls */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Month:
              </Typography>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Content based on active tab and view mode */}
      {viewMode === 'list' && activeTab === 0 && (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <ScheduleIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Schedule for {formatDate(selectedDate)}
              </Typography>
            </Box>
            
            {dailySchedules.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No schedule for this date
              </Typography>
            ) : (
              <List>
                {dailySchedules.map((schedule) => (
                  <React.Fragment key={schedule.id}>
                    <ListItem>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <BusinessIcon />
                      </Avatar>
                      <ListItemText
                        primary={schedule.campaign?.name || 'Unknown Campaign'}
                        secondary={
                          <Box component="div">
                            <Typography variant="body2" color="text.secondary" component="span" display="block">
                              {schedule.campaign?.description}
                            </Typography>
                            <Box component="div" display="flex" alignItems="center" mt={0.5}>
                              <TimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              <Typography variant="caption" component="span">
                                {schedule.startTimeLocal || schedule.startTime} - {schedule.endTimeLocal || schedule.endTime}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Weekly View */}
      {viewMode === 'list' && activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Weekly Schedule Overview
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Table sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Date</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Campaigns</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getWeekDates(selectedWeek).map((date) => {
                    const daySchedules = getSchedulesForDate(date, weeklySchedules);
                    return (
                      <TableRow key={date}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1 }} />
                            {formatDate(date)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minHeight: 40 }}>
                            {daySchedules.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No schedule
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  {daySchedules.length} schedule{daySchedules.length > 1 ? 's' : ''}
                                </Typography>
                                {daySchedules.map((schedule) => (
                                  <Chip
                                    key={schedule.id}
                                    label={`${schedule.campaign?.name} (${schedule.startTimeLocal || schedule.startTime}-${schedule.endTimeLocal || schedule.endTime})`}
                                    size="small"
                                    color="primary"
                                    sx={{ mb: 0.5, mr: 0.5 }}
                                  />
                                ))}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Monthly View */}
      {viewMode === 'list' && activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Monthly Schedule Overview
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Table sx={{ minWidth: 400 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Date</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Campaigns</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getMonthDates(selectedMonth).map((date) => {
                    const daySchedules = getSchedulesForDate(date, monthlySchedules);
                    const formattedDate = formatMonthDate(date);
                    return (
                      <TableRow key={date}>
                        <TableCell sx={{ minWidth: 100 }}>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                {formattedDate.dayName}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {formattedDate.day}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minHeight: 50, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {daySchedules.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No schedule
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                  {daySchedules.length} schedule{daySchedules.length > 1 ? 's' : ''}
                                </Typography>
                                {daySchedules.map((schedule) => (
                                  <Chip
                                    key={schedule.id}
                                    label={`${schedule.campaign?.name} (${schedule.startTimeLocal || schedule.startTime}-${schedule.endTimeLocal || schedule.endTime})`}
                                    size="small"
                                    color="primary"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                My Schedule - {(() => {
                  // If we're in weekly view (activeTab === 1), use the week start date to determine the month
                  if (activeTab === 1 && selectedWeek) {
                    const [year, month] = selectedWeek.split('-').map(Number);
                    const date = new Date(year, month - 1, 1);
                    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  }
                  // If we're in monthly view (activeTab === 2), use the selected month
                  if (activeTab === 2 && selectedMonth) {
                    // Parse the month string correctly to avoid timezone issues
                    const [year, month] = selectedMonth.split('-').map(Number);
                    const date = new Date(year, month - 1, 1); // month - 1 because Date constructor uses 0-based months
                    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  }
                  // Fallback to current month
                  const today = new Date();
                  const year = today.getFullYear();
                  const month = today.getMonth() + 1; // getMonth() returns 0-based, so add 1
                  const date = new Date(year, month - 1, 1); // month - 1 because Date constructor uses 0-based months
                  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                })()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const [year, month] = selectedMonth.split('-').map(Number);
                    const newDate = new Date(year, month - 2, 1);
                    const newMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
                    setSelectedMonth(newMonth);
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const [year, month] = selectedMonth.split('-').map(Number);
                    const newDate = new Date(year, month, 1);
                    const newMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`;
                    setSelectedMonth(newMonth);
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>

            {/* Calendar Grid */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: 1,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              {/* Calendar Headers */}
              {(() => {
                // If we're in weekly view, show the actual day names for the week
                if (activeTab === 1 && selectedWeek) {
                  return getWeekDates(selectedWeek).map((date, index) => {
                    // Parse date string to avoid timezone issues
                    const [year, month, day] = date.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    const dayName = localDate.toLocaleDateString('en-US', { weekday: 'short' });
                    return (
                      <Box
                        key={dayName}
                        sx={{
                          p: 1,
                          textAlign: 'center',
                          backgroundColor: '#f5f5f5',
                          fontWeight: 'bold',
                          borderBottom: '1px solid #e0e0e0'
                        }}
                      >
                        <Typography variant="body2">{dayName}</Typography>
                      </Box>
                    );
                  });
                } else {
                  // Show standard day headers for monthly view
                  return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                    <Box
                      key={day}
                      sx={{
                        p: 1,
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #e0e0e0'
                      }}
                    >
                      <Typography variant="body2">{day}</Typography>
                    </Box>
                  ));
                }
              })()}

              {/* Calendar Days */}
              {(() => {
                // If we're in weekly view (activeTab === 1), show weekly dates
                if (activeTab === 1 && selectedWeek) {
                  return getWeekDates(selectedWeek).map((date, index) => {
                    const isToday = date === selectedDate;
                    // Parse date string to avoid timezone issues
                    const [year, month, day] = date.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    const dayNumber = localDate.getDate();
                    
                    // Get my schedules for this date
                    const daySchedules = weeklySchedules ? weeklySchedules.filter(schedule => schedule.date === date) : [];

                    return (
                      <Box
                        key={date}
                        sx={{
                          minHeight: 120,
                          p: 1,
                          border: '1px solid #e0e0e0',
                          backgroundColor: 'white',
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#f0f0f0'
                          }
                        }}
                        onClick={() => {
                          setSelectedDate(date);
                          setViewMode('list');
                          setActiveTab(0);
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isToday ? 'bold' : 'normal',
                            color: isToday ? 'primary.main' : 'text.primary',
                            mb: 1
                          }}
                        >
                          {dayNumber}
                        </Typography>
                        
                        {/* Schedule Items */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {daySchedules.slice(0, 3).map((schedule, idx) => (
                            <Chip
                              key={`${schedule.id}-${idx}`}
                              label={`${schedule.campaign?.name || 'Unknown'} (${schedule.startTimeLocal || schedule.startTime}-${schedule.endTimeLocal || schedule.endTime})`}
                              size="small"
                              color="primary"
                              sx={{ 
                                fontSize: '0.65rem', 
                                height: 18,
                                '& .MuiChip-label': {
                                  px: 0.5
                                }
                              }}
                            />
                          ))}
                          {daySchedules.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{daySchedules.length - 3} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  });
                } else {
                  // Show monthly dates
                  return getMonthDates(selectedMonth).map((date, index) => {
                    const isCurrentMonth = date.startsWith(selectedMonth);
                    const isToday = date === selectedDate;
                    // Parse date string to avoid timezone issues
                    const [year, month, day] = date.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    const dayNumber = localDate.getDate();
                    
                    // Get my schedules for this date
                    const daySchedules = monthlySchedules ? monthlySchedules.filter(schedule => schedule.date === date) : [];

                return (
                  <Box
                    key={date}
                    sx={{
                      minHeight: 120,
                      p: 1,
                      border: '1px solid #e0e0e0',
                      backgroundColor: isCurrentMonth ? 'white' : '#f9f9f9',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isCurrentMonth ? '#f0f0f0' : '#f5f5f5'
                      }
                    }}
                    onClick={() => {
                      setSelectedDate(date);
                      setViewMode('list');
                      setActiveTab(0);
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isCurrentMonth ? (isToday ? 'primary.main' : 'text.primary') : 'text.secondary',
                        mb: 1
                      }}
                    >
                      {dayNumber}
                    </Typography>
                    
                    {/* Schedule Items */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {daySchedules.slice(0, 3).map((schedule, idx) => (
                        <Chip
                          key={`${schedule.id}-${idx}`}
                          label={`${schedule.campaign?.name || 'Unknown'} (${schedule.startTimeLocal || schedule.startTime}-${schedule.endTimeLocal || schedule.endTime})`}
                          size="small"
                          color="primary"
                          sx={{ 
                            fontSize: '0.65rem', 
                            height: 18,
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      ))}
                      {daySchedules.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{daySchedules.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              });
                }
              })()}
            </Box>

            {/* Legend */}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Legend:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: 'primary.main', borderRadius: 0.5 }} />
                  <Typography variant="caption">My Schedule</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#f0f0f0', border: '1px solid #ccc', borderRadius: 0.5 }} />
                  <Typography variant="caption">Other Month</Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default MySchedulePage;
